import { Image, Link, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { APP_HOST } from '@utils/apiHooks';

import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { absoluteFilePath } from '@utils/fn';
import { styles } from './styles';
import { useMemo } from 'react';
import { globalStyles } from '@composites/PDF/AgendaPdfContent/styles';
import { Ticket } from '@composites/Tickets';

interface TestimonialsBookNowProps {
  event: PublicEventDetailsResponse;
  tickets: Ticket[];
}

function chunkTestimonials<T>(items: T[], columnsCount: number): T[][] {
  const columns: T[][] = Array.from({ length: columnsCount }, () => []);
  items.forEach((item, index) => {
    columns[index % columnsCount].push(item);
  });
  return columns;
}

const TESTIMONIALS_LIMIT = 6;

const TestimonialsBookNow = ({ event, tickets }: TestimonialsBookNowProps) => {
  const { t } = useTranslation();

  const testimonialsItems = useMemo(
    () =>
      event?.website_content?.testimonials
        ?.filter(
          e =>
            e.is_partner &&
            e?.first_name &&
            e?.last_name &&
            e?.citation &&
            e?.company_name
        )
        ?.sort((a, b) => a.order - b.order)
        ?.map((e, id) => ({
          id: String(id),
          name: [e?.first_name, e?.last_name]?.filter(e => e).join(' '),
          jobTitle: e?.job_title,
          companyName: e?.company_name,
          videoLink: e?.video_url,
          logoLink: absoluteFilePath(e?.company_logo),
          citation: e?.citation,
        }))
        ?.slice(0, TESTIMONIALS_LIMIT),
    [event?.website_content?.testimonials]
  );

  const columns = useMemo(
    () => chunkTestimonials(testimonialsItems, 3),
    [testimonialsItems]
  );

  return (
    <View style={globalStyles.body}>
      <Text style={{ ...styles.titleText, color: event?.design?.themeColor }}>
        {t('this-is-what-our-participants-say')}
      </Text>
      <View
        style={{
          ...globalStyles.lineBar,
          borderColor: event?.design?.themeColor ?? 'black',
        }}
      />

      <View style={styles.testimonialsWrapper}>
        {columns.map((col, columnIndex) => (
          <View
            key={columnIndex}
            wrap={false}
            style={styles.testimonialsColumn}
          >
            {col.map(item => (
              <View key={item.id} wrap={false} style={styles.testimonialItem}>
                <Text style={styles.citationText}>
                  {'"'}
                  {item.citation}
                  {'"'}
                </Text>
                <Text style={styles.smallText}>{item.jobTitle}</Text>
                <Text style={styles.smallText}>{item.companyName}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>

      <Text style={{ ...styles.titleText, color: event?.design?.themeColor }}>
        {t('book-now')}
      </Text>
      <View
        style={{
          ...globalStyles.lineBar,
          borderColor: event?.design?.themeColor ?? 'black',
        }}
      />
      {tickets?.length > 0 && (
        <View style={styles.ticketsWrapper}>
          {tickets.map((ticket, index) => (
            <View key={index} wrap={false} style={styles.ticketItem}>
              <View style={styles.textWrapper}>
                <Text style={styles.ticketName}>{ticket.name}</Text>
                <Text style={styles.ticketPrice}>
                  {ticket.id === 'further-event-passes' || !ticket.price
                    ? '\u00A0'
                    : new Intl.NumberFormat('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                      }).format(ticket.price)}
                </Text>
              </View>

              <View
                style={{
                  ...styles.ticketDivider,
                  backgroundColor: event?.design?.themeColor || 'black',
                }}
              />
              <View
                style={{
                  ...styles.ticketButtonWrapper,
                }}
              >
                <Link
                  src={`${APP_HOST}/events/${event?.slug}${t('book-now-url')}`}
                  style={{
                    ...styles.ticketButton,
                    backgroundColor: event?.design?.themeColor || 'black',
                  }}
                >
                  {t('book-now')}
                </Link>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default TestimonialsBookNow;
